import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Form,
    FormCheck,
    FormControl,
    FormGroup,
    FormLabel,
    FormText,
} from "react-bootstrap";
import { TIMEZONES, TIMEZONES_MAP } from "../RetailerSettingsConstants";
import Api from "src/data/api";
import { useDispatch } from "react-redux";
import { showNotification } from "src/redux/notificationActions";
import { ERROR_MESSAGES, SETTINGS_MESSAGES } from "src/constants/messages";
import { NOTIFICATION_TYPES } from "src/constants/notificationTypes";
import { NOTIFICATION_HEADERS } from "src/constants/notificationHeaders";
import styles from "../RetailerSettings.module.scss";
import { COLOR_INPUT_PATTERN } from "src/components/RetailerWidgetSettings/RetailerWidgetSettingsConstants";

const SettingsBookingTools = ({
    retailerWebappUrl,
    token,
    zoomAccountEmail,
    zoomAppInstallUrl,
    pipedriveAppInstallUrl,
    retailerTimezone,
    retailerAppointmentInPersonOnly,
    retailerAppointmentVirtualOnly,
    isShopify,
    retailerCreateOderOnAppointmentCreation,
    retailerCreateDraftOderOnAppointmentCreation,
    webappThemeColor,
    webappThemeTextColor,
    webappButtonColor,
    webappButtonTextColor,
}) => {
    const dispatch = useDispatch();
    const [timezone, setTimezone] = useState(retailerTimezone);

    const [appointmentInPersonOnly, setAppointmentInPersonOnly] =
        useState(false);

    const [appointmentVirtualOnly, setAppointmentVirtualOnly] = useState(false);

    const [
        createOderOnAppointmentCreation,
        setCreateOderOnAppointmentCreation,
    ] = useState(false);

    const [
        createDraftOrderOnAppointmentCreation,
        setCreateDraftOrderOnAppointmentCreation,
    ] = useState(false);

    useEffect(() => {
        setAppointmentVirtualOnly(retailerAppointmentVirtualOnly);
        setAppointmentInPersonOnly(retailerAppointmentInPersonOnly);
        setCreateOderOnAppointmentCreation(
            retailerCreateOderOnAppointmentCreation
        );
        setCreateDraftOrderOnAppointmentCreation(
            retailerCreateDraftOderOnAppointmentCreation
        );
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            timezone,
            appointmentInPersonOnly,
            appointmentVirtualOnly,
            createOderOnAppointmentCreation,
            createDraftOrderOnAppointmentCreation,
            themeColor: webappThemeColor,
            themeTextColor: webappThemeTextColor,
            buttonColor: webappButtonColor,
            buttonTextColor: webappButtonTextColor,
        };
        try {
            await Api.request({
                method: "POST",
                url: `/retailer/settings/booking-tools`,
                data,
            });
            dispatch(
                showNotification(
                    SETTINGS_MESSAGES.UPDATE_RETAILER_SETTINGS_SUCCESS,
                    NOTIFICATION_TYPES.SUCCESS,
                    NOTIFICATION_HEADERS.RETAILER_SETTINGS
                )
            );
        } catch (error) {
            console.log(error);
            dispatch(
                showNotification(
                    ERROR_MESSAGES.SOMETHING_WENT_WRONG,
                    NOTIFICATION_TYPES.ERROR,
                    NOTIFICATION_HEADERS.OOPS
                )
            );
        }
    };

    return (
        <Form onSubmit={handleSubmit} className={styles.RetailerSettings}>
            <FormGroup controlId="input-email">
                <FormLabel>Virtual Appointments</FormLabel>
                <FormControl
                    as="select"
                    custom
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    name="timezone"
                >
                    {TIMEZONES.map((timezone) => (
                        <option key={timezone} value={timezone}>
                            {TIMEZONES_MAP[timezone]}
                        </option>
                    ))}
                </FormControl>
                <FormText muted>
                    This timezone will be used when creating virtual
                    appointments.
                </FormText>
            </FormGroup>
            <section>
                <h2>Virtual-only Appointments</h2>
                <p>
                    Changing this setting won't affect any in-person
                    appointments that have already been scheduled.
                </p>

                <FormCheck
                    custom
                    type="checkbox"
                    id="input-virtual-only"
                    label="Only allow virtual appointments"
                    value={appointmentVirtualOnly}
                    checked={appointmentVirtualOnly}
                    name={`appointmentVirtualOnly`}
                    onChange={() => setAppointmentVirtualOnly((prev) => !prev)}
                />
            </section>
            <section>
                <h2>In-person only appointments</h2>
                <p>
                    Changing this setting won't affect any virtual appointments
                    that have already been scheduled.
                </p>
                <FormCheck
                    custom
                    type="checkbox"
                    id="input-in-person-only"
                    label="Only allow in-person appointments"
                    value={appointmentInPersonOnly}
                    checked={appointmentInPersonOnly}
                    name={`appointmentInPersonOnly`}
                    onChange={() => setAppointmentInPersonOnly((prev) => !prev)}
                />
            </section>

            {isShopify && (
                <section>
                    <h2>Create order on appointment creation</h2>
                    <FormCheck
                        custom
                        type="checkbox"
                        id="input-create-order-on-appointment-creation"
                        label="Create order on appointment creation"
                        value={createOderOnAppointmentCreation}
                        checked={createOderOnAppointmentCreation}
                        name={`createOderOnAppointmentCreation`}
                        onChange={() =>
                            setCreateOderOnAppointmentCreation((prev) => !prev)
                        }
                    />
                </section>
            )}

            {isShopify && (
                <section>
                    <h2>Create draft order on appointment creation</h2>
                    <FormCheck
                        custom
                        type="checkbox"
                        id="input-create-draft-order-on-appointment-creation"
                        label="Create draft order on appointment creation"
                        value={createDraftOrderOnAppointmentCreation}
                        checked={createDraftOrderOnAppointmentCreation}
                        name={`createDraftOrderOnAppointmentCreation`}
                        onChange={() =>
                            setCreateDraftOrderOnAppointmentCreation(
                                (prev) => !prev
                            )
                        }
                    />
                </section>
            )}

            {token && (
                <section>
                    <h2>Zoom Integration</h2>
                    <a target={"_blank"} href={zoomAppInstallUrl}>
                        Click here to add a Zoom account to your calendar
                        invites for virtual appointments
                    </a>
                </section>
            )}
            {zoomAccountEmail && (
                <p>
                    A Zoom account has already been connected :{" "}
                    {zoomAccountEmail}
                </p>
            )}
            <section>
                <h2>Pipedrive Integration</h2>
                <a target={"_blank"} href={pipedriveAppInstallUrl}>
                        Click here to add a Pipedrive account 
                </a>
            </section>
            <Button type="submit" className="mb-4">
                Save
            </Button>
        </Form>
    );
};

export default SettingsBookingTools;
